import React, { MouseEvent, useCallback, useRef, useState } from 'react';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { styled } from '@mui/material';
import { Box, Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import SplitFabProps from './SplitFab.props';

const PREFIX = 'SplitFab';

const classes = {
  root: `${PREFIX}-root`,
  button: `${PREFIX}-button`,
  icon: `${PREFIX}-icon`,
  label: `${PREFIX}-label`,
  leftButton: `${PREFIX}-leftButton`,
  rightButton: `${PREFIX}-rightButton`,
  iconOpen: `${PREFIX}-iconOpen`,
  popper: `${PREFIX}-popper`,
  paper: `${PREFIX}-paper`,
  menuList: `${PREFIX}-menuList`,
  menuItem: `${PREFIX}-menuItem`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'fixed',
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 3,
  },

  [`& .${classes.button}`]: {
    boxShadow: 'none',
  },

  [`& .${classes.icon}`]: {
    paddingRight: theme.spacing(1),
  },

  [`& .${classes.label}`]: {
    fontSize: '0.9375rem',
    fontWeight: 'bold',
  },

  [`& .${classes.leftButton}`]: {
    borderRadius: '2.6875rem 0 0 2.6875rem',
  },

  [`& .${classes.rightButton}`]: {},

  [`& .${classes.iconOpen}`]: {
    transform: 'rotate(180deg)',
  },

  [`& .${classes.popper}`]: {
    zIndex: 1,
  },

  [`& .${classes.paper}`]: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },

  [`& .${classes.menuList}`]: {
    padding: '0',
    margin: '-1rem 0',
  },

  [`& .${classes.menuItem}`]: {
    fontSize: '1rem',
    fontWeight: 'bold',
    borderRadius: '2.6875rem',
    margin: '1rem 0',
    color: '#ffff',
    backgroundColor: theme.palette.primary.main,
    textTransform: 'uppercase',
    height: '2.5rem',
    width: 'fit-content',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const SplitFab = ({ label, options, children: icon, mainAction, disabled = false }: SplitFabProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    setOpen(prevOpen => !prevOpen);
  }, []);

  const handleClose = useCallback(
    (event: MouseEvent<Document>) => {
      if (anchorRef.current?.contains(event.target as Node)) {
        return;
      }

      setOpen(false);
    },
    [anchorRef]
  );

  return (
    <StyledBox className={classes.root}>
      <ButtonGroup
        color="primary"
        className={classes.button}
        variant="contained"
        ref={anchorRef}
        aria-label="split button">
        <Button onClick={mainAction} className={classes.leftButton} disabled={disabled}>
          {icon}
          {label}
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
          className={classes.rightButton}
          disabled={disabled}>
          <ArrowDropDownIcon className={open ? classes.iconOpen : ''} />
        </Button>
      </ButtonGroup>
      <Popper
        className={classes.popper}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper className={classes.paper}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" className={classes.menuList}>
                  {options.map(option => (
                    <MenuItem
                      key={option.label}
                      onClick={option.callback}
                      className={classes.menuItem}
                      disabled={!option.callback}>
                      {icon}
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </StyledBox>
  );
};

export default SplitFab;
